<script setup lang="ts">
import { formatCnpjMasked, normalizeCnpjAlphanumeric } from '@solfacil/frontend-utils/cnpj';
import { formatCpfMasked, normalizeCpfDigits } from '@solfacil/frontend-utils/cpf';
import { Building, UserSquare2 } from 'lucide-vue-next';
import { computed } from 'vue';

import Divider from '~/components/shared/Divider.vue';
import DocumentViewer from '~/components/shared/DocumentViewer.vue';
import InfoGridItem from '~/components/shared/InfoGridItem.vue';
import { DATE_FORMATS } from '~/constants.ts';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import date from '~/utils/date';
import { formatPhoneForDisplay } from '~/utils/formatters';

import type { CustomerRegisterApi } from '../../services/customer-register-api/customer-register-api.types';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const projectId = computed(() => props.financing.project_id);
const registrationQuery = useCustomerRegistrationDetails(projectId);

const registrationData = computed(() => registrationQuery.data.value);
const internalIsLoading = computed(
  () => props.isLoading || registrationQuery.isLoading.value || registrationQuery.isFetching.value,
);

const formatDate = (dateString?: string) => {
  if (!dateString) return null;
  return date(dateString).format(DATE_FORMATS.DATE);
};

const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) return null;
  return value ? 'Sim' : 'Não';
};

const customerData = computed(() => {
  const data = registrationData.value;
  if (!data) return [];

  const items: Array<{ label: string; value: null | string }> = [];

  if (data.person_type === 'pf' && data.customer) {
    items.push(
      { label: 'Nome Completo', value: data.customer.full_name || null },
      { label: 'CPF', value: data.customer.cpf ? formatCpfMasked(normalizeCpfDigits(data.customer.cpf)) : null },
      { label: 'Data de Nascimento', value: formatDate(data.customer.birthdate) },
      { label: 'E-mail', value: data.customer.contact?.email || null },
      {
        label: 'Telefone',
        value: data.customer.contact?.phone
          ? formatPhoneForDisplay(data.customer.contact.phone) : null,
      },
    );
  }
  if (data.person_type === 'pj' && data.company) {
    items.push(
      { label: 'Razão Social', value: data.company.company_name || null },
      {
        label: 'CNPJ',
        value: data.company.cnpj
          ? formatCnpjMasked(normalizeCnpjAlphanumeric(data.company.cnpj)) : null,
      },
      { label: 'Data de Fundação', value: formatDate(data.company.foundation_date) },
      { label: 'E-mail', value: data.company.contact?.email || null },
      {
        label: 'Telefone',
        value: data.company.contact?.phone
          ? formatPhoneForDisplay(data.company.contact.phone) : null,
      },
    );

    if (data.company.majority_partner) {
      items.push(
        { label: 'Sócio Majoritário - Nome', value: data.company.majority_partner.full_name || null },
        {
          label: 'Sócio Majoritário - CPF',
          value: data.company.majority_partner.cpf
            ? formatCpfMasked(normalizeCpfDigits(data.company.majority_partner.cpf))
            : null,
        },
        { label: 'Sócio Majoritário - Data de Nascimento', value: formatDate(data.company.majority_partner.birthdate) },
        { label: 'Sócio Majoritário - E-mail', value: data.company.majority_partner.contact?.email || null },
        {
          label: 'Sócio Majoritário - Telefone',
          value: data.company.majority_partner.contact?.phone
            ? formatPhoneForDisplay(data.company.majority_partner.contact.phone)
            : null,
        },
        { label: 'É Representante Legal', value: formatBoolean(data.company.majority_partner.is_legal_representative) },
      );
    }
  }

  return items.filter((item) => item.value !== null);
});

type DocumentGroup = {
  documents: Array<{
    document: CustomerRegisterApi.Document;
    label: string;
  }>;
  type: 'CNH' | 'CNH Digital' | 'RG';
};

const groupDocumentsByType = (documents: CustomerRegisterApi.Document[]): DocumentGroup[] => {
  const groups: DocumentGroup[] = [];

  const cnhDigital = documents.find((doc) => doc.type === 'digital_driver_license');
  if (cnhDigital) {
    groups.push({ documents: [{ document: cnhDigital, label: 'CNH Digital' }], type: 'CNH Digital' });
  }

  const cnhFront = documents.find((doc) => doc.type === 'driver_license_front');
  const cnhBack = documents.find((doc) => doc.type === 'driver_license_back');
  if (cnhFront || cnhBack) {
    const cnhDocs: Array<{ document: CustomerRegisterApi.Document; label: string }> = [];
    if (cnhFront) cnhDocs.push({ document: cnhFront, label: 'Frente da CNH' });
    if (cnhBack) cnhDocs.push({ document: cnhBack, label: 'Verso da CNH' });
    groups.push({ documents: cnhDocs, type: 'CNH' });
  }

  const rgFront = documents.find((doc) => doc.type === 'identity_document_front');
  const rgBack = documents.find((doc) => doc.type === 'identity_document_back');
  if (rgFront || rgBack) {
    const rgDocs: Array<{ document: CustomerRegisterApi.Document; label: string }> = [];
    if (rgFront) rgDocs.push({ document: rgFront, label: 'Frente do RG' });
    if (rgBack) rgDocs.push({ document: rgBack, label: 'Verso do RG' });
    groups.push({ documents: rgDocs, type: 'RG' });
  }

  return groups;
};

const customerDocumentGroups = computed(() => {
  const data = registrationData.value;
  if (!data) return [];

  let documents: CustomerRegisterApi.Document[] = [];

  if (data.person_type === 'pf' && data.customer?.documents) {
    documents = data.customer.documents;
  } else if (data.person_type === 'pj' && data.company?.documents) {
    documents = data.company.documents;
  }

  return groupDocumentsByType(documents);
});

const guarantorData = computed(() => {
  const data = registrationData.value?.guarantor;
  if (!data) return [];

  return [
    { label: 'Nome Completo', value: data.full_name || null },
    { label: 'CPF', value: data.cpf ? formatCpfMasked(normalizeCpfDigits(data.cpf)) : null },
    { label: 'Data de Nascimento', value: formatDate(data.birthdate) },
    { label: 'E-mail', value: data.contact?.email || null },
    { label: 'Telefone', value: data.contact?.phone ? formatPhoneForDisplay(data.contact.phone) : null },
  ].filter((item) => item.value !== null);
});

const guarantorDocumentGroups = computed(() => {
  const documents = registrationData.value?.guarantor?.documents || [];
  return groupDocumentsByType(documents);
});

const hasData = computed(() => (
  customerData.value.length > 0
  || customerDocumentGroups.value.length > 0
  || registrationData.value?.guarantor
  || guarantorDocumentGroups.value.length > 0
));

const skeletonCustomerData = computed(() => {
  if (!internalIsLoading.value) return [];
  return [
    { label: 'Nome Completo', value: null },
    { label: 'CPF', value: null },
    { label: 'Data de Nascimento', value: null },
    { label: 'E-mail', value: null },
    { label: 'Telefone', value: null },
  ];
});
</script>

<template>
  <div class="customer-data-content">
    <div v-if="!internalIsLoading && !hasData" class="empty-state">
      <p class="empty-message">Dados ainda não preenchidos</p>
    </div>

    <div v-else class="datagrid">
      <InfoGridItem
        v-for="item in (internalIsLoading ? skeletonCustomerData : customerData)"
        :key="item.label"
        :label="item.label"
        :value="item.value"
        :is-loading="internalIsLoading"
      />

      <InfoGridItem label="Tipo de Pessoa" :is-loading="internalIsLoading">
        <template v-if="financing.person_type === 'PF'">
          <div class="d-flex align-items-center gap-2">
            <UserSquare2 class="icon icon-2 text-primary" />
            <span>Pessoa Física</span>
          </div>
        </template>
        <template v-else-if="financing.person_type === 'PJ'">
          <div class="d-flex align-items-center gap-2">
            <Building class="icon icon-2 text-primary" />
            <span>Pessoa Jurídica</span>
          </div>
        </template>
      </InfoGridItem>

      <template v-if="registrationData?.guarantor">
        <div class="section-divider" />
        <div class="section-title-wrapper">
          <h5 class="section-title">Dados do Fiador</h5>
          <Divider />
        </div>
        <InfoGridItem
          v-for="item in guarantorData"
          :key="`guarantor-${item.label}`"
          :label="item.label"
          :value="item.value"
          :is-loading="internalIsLoading"
        />
      </template>
    </div>

    <div
      v-if="internalIsLoading || customerDocumentGroups.length > 0 || guarantorDocumentGroups.length > 0"
      class="documents-row"
    >
      <div v-if="internalIsLoading || customerDocumentGroups.length > 0" class="documents-section">
        <div class="section-title-wrapper">
          <h5 class="section-title">Documentos do Cliente</h5>
          <Divider />
        </div>
        <div
          v-for="group in customerDocumentGroups"
          :key="group.type"
          class="document-group"
        >
          <InfoGridItem
            label="Tipo de Documento"
            :value="group.type"
            :is-loading="internalIsLoading"
          />
          <div class="documents-list">
            <template v-if="internalIsLoading">
              <div
                v-for="i in 2"
                :key="`skeleton-${i}`"
                class="document-item-wrapper"
              >
                <span class="skeleton skeleton-label" />
                <DocumentViewer :is-loading="true" />
              </div>
            </template>
            <template v-else>
              <div
                v-for="docWithLabel in group.documents"
                :key="docWithLabel.document.id"
                class="document-item-wrapper"
              >
                <span class="document-label">{{ docWithLabel.label }}</span>
                <DocumentViewer :document="docWithLabel.document" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <div v-if="internalIsLoading || guarantorDocumentGroups.length > 0" class="documents-section">
        <div class="section-title-wrapper">
          <h5 class="section-title">Documentos do Fiador</h5>
          <Divider />
        </div>
        <div
          v-for="group in guarantorDocumentGroups"
          :key="group.type"
          class="document-group"
        >
          <InfoGridItem
            label="Tipo de Documento"
            :value="group.type"
            :is-loading="internalIsLoading"
          />
          <div class="documents-list">
            <template v-if="internalIsLoading">
              <div
                v-for="i in 2"
                :key="`skeleton-${i}`"
                class="document-item-wrapper"
              >
                <span class="skeleton skeleton-label" />
                <DocumentViewer :is-loading="true" />
              </div>
            </template>
            <template v-else>
              <div
                v-for="docWithLabel in group.documents"
                :key="docWithLabel.document.id"
                class="document-item-wrapper"
              >
                <span class="document-label">{{ docWithLabel.label }}</span>
                <DocumentViewer :document="docWithLabel.document" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.customer-data-content {
  .datagrid {
    display: grid;
    gap: 0.75rem;
  }

  .section-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    width: 100%;
    min-width: 0;
    overflow: hidden;

    :deep(.divider) {
      flex: 1;
      min-width: 0;
      width: auto;
    }
  }

  .section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }

  .section-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 1.5rem 0;
  }

  .documents-row {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .documents-section {
    width: 100%;
  }

  .document-group {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .documents-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.75rem;
  }

  .document-item-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    min-width: 250px;
  }

  .document-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #64748b;
  }

  .skeleton-label {
    width: 100px;
    height: 0.8125rem;
    border-radius: 0.25rem;
  }

  .empty-state {
    padding: 2rem;
    text-align: center;
  }

  .empty-message {
    color: #94a3b8;
    font-size: 0.9375rem;
    font-style: italic;
    margin: 0;
  }
}
</style>
